import React from 'react';
import { Theme } from '@mui/material';
import { designTokens } from '../themes/design-tokens';

// Extend the MUI theme to include DataGrid component overrides
declare module '@mui/material/styles' {
  interface Components {
    MuiDataGrid?: {
      styleOverrides?: {
        root?:
          | React.CSSProperties
          | ((props: { theme: Theme }) => React.CSSProperties);
      };
    };
  }
}

export const dataGridTheme = {
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        // Main DataGrid container
        border: 'none',
        borderRadius: theme.spacing(1),
        fontFamily: designTokens.typography.fontFamily.primary,
        width: '100%',
        overflow: 'hidden',

        // Footer container - full width
        '& .MuiDataGrid-footerContainer': {
          display: 'flex !important',
          justifyContent: 'center !important',
          alignItems: 'center !important',
          borderTop: `1px solid ${theme.palette.divider}`,
          padding: '0 !important',
          margin: '0 !important',
          minHeight: '56px',
          width: '100% !important',
          maxWidth: '100% !important',
          boxSizing: 'border-box !important',
          position: 'relative',
          left: 0,
          right: 0,

          // Ensure custom pagination takes full width and stretches
          '& > *': {
            width: '100% !important',
            maxWidth: '100% !important',
            display: 'flex !important',
            alignItems: 'center',
            margin: '0 !important',
            padding: '0 !important',
            boxSizing: 'border-box',
          },
        },

        // Hide default pagination since we're using custom pagination
        '& .MuiTablePagination-root': {
          display: 'none !important',
        },

        // Header styling
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.divider}`,

          '& .MuiDataGrid-columnHeader': {
            fontFamily: designTokens.typography.fontFamily.primary,
            fontSize: '0.75rem',
            fontWeight: designTokens.typography.fontWeights.medium,
            color: theme.palette.text.secondary,

            '&:focus, &:focus-within': {
              outline: 'none',
            },
          },
        },

        // Cell styling
        '& .MuiDataGrid-cell': {
          fontFamily: designTokens.typography.fontFamily.primary,
          fontSize: '0.875rem',
          fontWeight: designTokens.typography.fontWeights.regular,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,

          '&:focus, &:focus-within': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '-2px',
          },
        },

        // Row styling
        '& .MuiDataGrid-row': {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },

          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,

            '&:hover': {
              backgroundColor: theme.palette.primary.light,
            },
          },
        },
      }),
    },
  },
};
